import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useDeliverySettingsStore } from './deliverySettingsStore';
export interface CartItem {
  productId: string;
  quantity: number;
  price: number;
  name: string;
  nameBn: string;
  image: string;
  weight: number;
  stock: number;
}

interface CartState {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getDeliveryFee: () => number;
  getFinalTotal: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) => {
        set((state) => {
          const existingItem = state.items.find((i) => i.productId === item.productId);
          
          if (existingItem) {
            const newQuantity = Math.min(
              existingItem.quantity + (item.quantity || 1),
              item.stock
            );
            return {
              items: state.items.map((i) =>
                i.productId === item.productId
                  ? { ...i, quantity: newQuantity }
                  : i
              ),
            };
          }
          
          return {
            items: [
              ...state.items,
              { ...item, quantity: Math.min(item.quantity || 1, item.stock) },
            ],
          };
        });
      },

      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter((i) => i.productId !== productId),
        }));
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }
        
        set((state) => ({
          items: state.items.map((i) =>
            i.productId === productId
              ? { ...i, quantity: Math.min(quantity, i.stock) }
              : i
          ),
        }));
      },

      clearCart: () => {
        set({ items: [] });
      },

      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },

      getTotalPrice: () => {
        return get().items.reduce(
          (total, item) => total + item.price * item.quantity,
          0
        );
      },

      getDeliveryFee: () => {
        const subtotal = get().getTotalPrice();
        const config = useDeliverySettingsStore.getState().config;
        return subtotal >= config.freeDeliveryThreshold ? 0 : config.baseDeliveryFee;
      },

      getFinalTotal: () => {
        return get().getTotalPrice() + get().getDeliveryFee();
      },
    }),
    {
      name: 'chocolate-vibes-cart',
    }
  )
);
